import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { WalletContext } from "../context/WalletContext";

const API_URL = "https://bitspend-api.onrender.com/api";

export default function MerchantTransactions() {
  const navigate = useNavigate();
  const { selectedWallet } = useContext(WalletContext);
  
  const [merchantAddress, setMerchantAddress] = useState(selectedWallet || "");
  const [transactions, setTransactions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(!selectedWallet);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Pagination
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!selectedWallet) {
      setError("Please connect your wallet first");
      return;
    }
    setMerchantAddress(selectedWallet);
    setShowManualEntry(false);
  }, [selectedWallet]);

  useEffect(() => {
    if (merchantAddress && !showManualEntry) {
      fetchTransactions();
    }
  }, [merchantAddress, offset, statusFilter, dateFrom, dateTo]);

  const fetchTransactions = async () => {
    setLoading(true);
    setError("");

    try {
      let url = `${API_URL}/transactions/merchant/${merchantAddress}?limit=${limit}&offset=${offset}`;
      
    if (statusFilter !== "all") {
      url += `&status=${statusFilter}`;
    }
      if (dateFrom) {
        url += `&fromDate=${dateFrom}`;
      }
      if (dateTo) {
        url += `&toDate=${dateTo}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch transactions");
      }

      const data = await response.json();

      if (data.success) {
        setTransactions(data.data.transactions);
        setStatistics(data.data.statistics);
        setHasMore(data.data.pagination.hasMore);
      } else {
        throw new Error(data.error || "Failed to load transactions");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMerchant = () => {
    if (!merchantAddress.trim()) {
      setError("Please enter a merchant address");
      return;
    }
    setShowManualEntry(false);
    setOffset(0);
    fetchTransactions();
  };

  const handleLoadMore = () => {
    setOffset(prev => prev + limit);
  };

  const handleLoadPrevious = () => {
    setOffset(prev => Math.max(0, prev - limit));
  };

  const handleResetFilters = () => {
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
    setOffset(0);
  };

  const truncateHash = (hash) => {
    if (!hash) return "****";
    return `${hash.slice(0, 8)}...${hash.slice(-6)}`;
  };

  const truncateAddress = (addr) => {
    if (!addr) return "****";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "text-green-400 bg-green-500/20";
      case "pending":
        return "text-yellow-400 bg-yellow-500/20";
      case "failed":
        return "text-red-400 bg-red-500/20";
      default:
        return "text-gray-400 bg-gray-500/20";
    }
  };

  // Manual Entry View
  if (showManualEntry) {
    return (
      <div className="min-h-screen flex flex-col p-6 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <Header />
        
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-full max-w-md">
            <h1 className="text-2xl font-bold text-center mb-6">
              Merchant Transaction History
            </h1>

            <div className="bg-gray-800 rounded-2xl p-6 shadow-lg">
              <label className="block text-gray-400 text-sm mb-2">
                Merchant Address
              </label>
              <input
                type="text"
                value={merchantAddress}
                onChange={(e) => {
                  setMerchantAddress(e.target.value);
                  setError("");
                }}
                placeholder="0x..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={handleLoadMerchant}
                disabled={!merchantAddress.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 py-3 rounded-xl font-semibold transition"
              >
                Load Transactions
              </button>

              <button
                onClick={() => navigate("/")}
                className="w-full mt-3 bg-gray-700 hover:bg-gray-600 py-3 rounded-xl font-semibold transition"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Transaction History View
  return (
    <div className="p-6 min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-gray-800 text-white">
      <Header />
      
      <div className="flex-1 py-6 max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => setShowManualEntry(true)}
            className="text-blue-400 hover:text-blue-300 text-sm mb-2"
          >
            ← Change Merchant
          </button>
          <h1 className="text-2xl font-bold mb-2">Merchant Transactions</h1>
          <p className="text-gray-400 text-sm font-mono">
            {truncateAddress(merchantAddress)}
          </p>
        </div>

        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-gray-400 text-xs mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-green-400">
                ${parseFloat(statistics.totalRevenue).toFixed(2)}
              </p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-gray-400 text-xs mb-1">Transactions</p>
              <p className="text-2xl font-bold text-white">
                {statistics.totalTransactions}
              </p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-gray-400 text-xs mb-1">Unique Cards</p>
              <p className="text-2xl font-bold text-blue-400">
                {statistics.uniqueCards}
              </p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-gray-400 text-xs mb-1">Success Rate</p>
              <p className="text-2xl font-bold text-green-400">
                {statistics.successRate || "0%"}
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-500/20 border border-red-500 rounded-xl p-4 mb-6">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Transactions List */}
        {!loading && !error && transactions.length === 0 && (
          <div className="bg-gray-800 rounded-xl p-8 text-center">
            <p className="text-gray-400">No transactions found</p>
          </div>
        )}

        {!loading && !error && transactions.length > 0 && (
          <>
            <div className="space-y-3 mb-6">
              {transactions.map((tx, index) => (
                <div
                  key={tx.txHash || index}
                  className="bg-gray-800 rounded-xl p-4 hover:bg-gray-750 transition"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold">
                          +${parseFloat(tx.amount).toFixed(2)} MUSD
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded ${getStatusColor(
                            tx.status
                          )}`}
                        >
                          {tx.status}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs">
                        {new Date(tx.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-400">From Card:</span>
                      <span className="text-blue-400 font-mono">
                        {tx.cardId || truncateAddress(tx.cardAddress)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tx Hash:</span>
                      <a
                        href={tx.explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 font-mono"
                      >
                        {truncateHash(tx.txHash)}
                      </a>
                    </div>
                    {tx.merchantId && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Merchant ID:</span>
                        <span className="text-gray-300 font-mono text-xs">
                          {tx.merchantId}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex gap-3">
              {offset > 0 && (
                <button
                  onClick={handleLoadPrevious}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 py-3 rounded-xl font-semibold transition"
                >
                  ← Previous
                </button>
              )}
              {hasMore && (
                <button
                  onClick={handleLoadMore}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 py-3 rounded-xl font-semibold transition"
                >
                  Load More →
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
